import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { CATEGORIES, PRIORITIES, categoryColor, categoryLabel, formatDateTime, stripMentionIds } from "../lib/constants";
import { CheckCircle2, ChevronUp, ChevronDown, Trash2, X, Users, User, Plus, Calendar, GitMerge, AlertTriangle, Search, CalendarPlus, CalendarCheck, Play, UserPlus, RotateCcw, ClipboardCheck, ArrowRight, Inbox, Clock, SlidersHorizontal, Ban, Video, MapPin, AlarmClock, MoreHorizontal, Pause } from "lucide-react";
import { inviteIsCurrent } from "../lib/calendarPrefs";
import { toast } from "sonner";
import { useConfirm } from "../components/Confirm";
import DeadlinePicker from "../components/DeadlinePicker";
import TaskDrawer, { Select } from "../components/TaskDrawer";
import { EXIT_MS, wait, snoozeOptions, shortWhen, shortTitle } from "../lib/taskActions";

const PRIORITY_ORDER = { urgent: 0, high: 1, medium: 2, low: 3 };
const ACTIVE = new Set(["pending", "in_progress"]);

// Urgency buckets, in the order they appear on the page. "attention" is a
// virtual bucket (overdue + today) used by dashboard deep links.
const BUCKETS = [
  { key: "overdue",     label: "Overdue",     tone: "red",   hint: "Past their deadline — deal with these first." },
  { key: "today",       label: "Due today",   tone: "amber", hint: "Due before midnight." },
  { key: "tomorrow",    label: "Tomorrow",    tone: "blue",  hint: "" },
  { key: "week",        label: "This week",   tone: "blue",  hint: "" },
  { key: "later",       label: "Later",       tone: "slate", hint: "" },
  { key: "no_deadline", label: "No deadline", tone: "amber", hint: "These drift if nobody dates them — set a deadline." },
];

const TONE = {
  red:   { bar: "bg-red-500",   head: "text-red-700",   chipOn: "bg-red-600 text-white border-red-600",     chipOff: "bg-red-50 text-red-700 border-red-200 hover:border-red-400" },
  amber: { bar: "bg-amber-500", head: "text-amber-700", chipOn: "bg-amber-600 text-white border-amber-600", chipOff: "bg-amber-50 text-amber-800 border-amber-200 hover:border-amber-400" },
  blue:  { bar: "bg-blue-500",  head: "text-blue-700",  chipOn: "bg-blue-600 text-white border-blue-600",   chipOff: "bg-blue-50 text-blue-700 border-blue-200 hover:border-blue-400" },
  slate: { bar: "bg-slate-400", head: "text-slate-600", chipOn: "bg-slate-900 text-white border-slate-900", chipOff: "bg-white text-slate-700 border-slate-200 hover:border-slate-400" },
};

// Group sections can't use overflow-hidden (row popovers must escape them), so
// round the last row's corners by hand.
const ROUNDED_LAST = "[&>div:last-child_[data-row]]:rounded-b-xl [&>div:last-child_[data-bar]]:rounded-bl-xl";

const PRIORITY_BAR = { urgent: "bg-red-500", high: "bg-amber-500", medium: "bg-blue-400", low: "bg-slate-300" };

function startOfDay(d) { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }

function bucketOf(task, b) {
  if (!task.deadline) return "no_deadline";
  const d = new Date(task.deadline);
  if (d < b.now) return "overdue";
  if (d < b.tomorrow) return "today";
  if (d < b.dayAfter) return "tomorrow";
  if (d < b.weekEnd) return "week";
  return "later";
}

const isMeeting = (t) => (t?.item_type || "task") === "meeting";

// "Overdue by 2 h", "Due in 45 min", "Starts in 45 min", "Starts tomorrow 10:00 AM".
function dueLabel(iso, b, meeting = false) {
  if (!iso) return null;
  const verb = meeting ? "Starts" : "Due";
  const d = new Date(iso);
  const ms = d.getTime() - b.now.getTime();
  const mins = Math.round(Math.abs(ms) / 60000);
  const span = mins < 60 ? `${mins} min` : mins < 60 * 24 ? `${Math.floor(mins / 60)} h${mins % 60 ? ` ${mins % 60} min` : ""}` : `${Math.round(mins / 1440)} day${Math.round(mins / 1440) === 1 ? "" : "s"}`;
  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  if (ms < 0) return { text: meeting ? `Started ${span} ago` : `Overdue by ${span}`, sub: formatDateTime(iso), tone: "red" };
  if (d < b.tomorrow) return { text: `${verb} in ${span}`, sub: `today ${time}`, tone: mins < 120 ? "amber" : "slate" };
  if (d < b.dayAfter) return { text: `${verb} tomorrow ${time}`, sub: null, tone: "slate" };
  if (d < b.weekEnd) return { text: `${verb} ${d.toLocaleDateString("en-US", { weekday: "short" })} ${time}`, sub: null, tone: "slate" };
  return { text: `${verb} ${formatDateTime(iso)}`, sub: null, tone: "slate" };
}

export default function Tasks() {
  const confirm = useConfirm();
  const [params, setParams] = useSearchParams();
  const [tasks, setTasks] = useState([]);
  const [reviewCount, setReviewCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [focusTask, setFocusTask] = useState(null);
  const [showCreateTask, setShowCreateTask] = useState(() => params.get("create") === "1");

  // view: what kind of tasks; bucket: urgency slice within the active view.
  // Legacy dashboard links use ?tab=action|active|in_progress|completed|rejected.
  const [view, setView] = useState(() => {
    const t = params.get("tab") || params.get("view");
    if (t === "completed" || t === "rejected") return t;
    return "active";
  });
  const [bucket, setBucket] = useState(() => {
    const t = params.get("tab");
    const b = params.get("bucket");
    if (b && (BUCKETS.some((x) => x.key === b) || b === "attention")) return b;
    return t === "action" ? "attention" : "all";
  });
  const [onlyInProgress, setOnlyInProgress] = useState(() => params.get("tab") === "in_progress");
  const [selectedPriority, setSelectedPriority] = useState(() => params.get("priority") || "");
  const [selectedType, setSelectedType] = useState(() => params.get("type") || "");
  const [selectedAssignee, setSelectedAssignee] = useState("");
  const [assignees, setAssignees] = useState([]);
  const [settingDeadlineTaskId, setSettingDeadlineTaskId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showBulkMenu, setShowBulkMenu] = useState(false);
  const [bulkMenuView, setBulkMenuView] = useState("main"); // "main" | "assign" | "priority"
  const [bulkRescheduling, setBulkRescheduling] = useState(false);
  // Row popover: { id, kind: "snooze" | "assign" | "more" } — one open at a time.
  const [menuFor, setMenuFor] = useState(null);
  const [exiting, setExiting] = useState({}); // id → "done" | "rejected" | "reopened" | "snoozed" | "deleted"
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [showFilters, setShowFilters] = useState(false);

  const filters = useMemo(() => ({
    category: params.get("category") || "",
  }), [params]);

  const load = async () => {
    setLoading(true);
    try {
      const q = { in_review: false };
      if (filters.category) q.category = filters.category;
      const [r, rv] = await Promise.all([
        api.get("/tasks", { params: q }),
        api.get("/tasks", { params: { in_review: true } }).catch(() => ({ data: { tasks: [] } })),
      ]);
      setTasks(r.data.tasks);
      setReviewCount((rv.data.tasks || []).length);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filters.category]);
  useEffect(() => {
    const onChanged = () => load();
    window.addEventListener("brainydo:tasks-changed", onChanged);
    return () => window.removeEventListener("brainydo:tasks-changed", onChanged);
  }, [filters.category]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get("/settings/assignees").then((r) => setAssignees(r.data.assignees || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const focusId = params.get("focus");
    if (focusId && tasks.length) {
      const t = tasks.find((x) => x.id === focusId);
      if (t) setFocusTask(t);
    }
  }, [params, tasks]);

  const setFilter = (key, value) => {
    const np = new URLSearchParams(params);
    if (value) np.set(key, value); else np.delete(key);
    setParams(np);
  };

  const applyTask = (data) => {
    setTasks((cur) => cur.map((t) => (t.id === data.id ? data : t)));
    if (focusTask?.id === data.id) setFocusTask(data);
  };

  const updateTask = async (id, patch, { silent = false } = {}) => {
    try {
      const r = await api.patch(`/tasks/${id}`, patch);
      applyTask(r.data);
      if (!silent) toast.success("Saved");
      return r.data;
    } catch { toast.error("Save failed"); return null; }
  };

  // Patch a task while its row plays an exit animation, and only swap in the
  // result once both finish — so the row slides away instead of blinking out.
  const exitTask = async (id, kind, patch) => {
    setExiting((cur) => ({ ...cur, [id]: kind }));
    let data = null;
    try {
      const [r] = await Promise.all([api.patch(`/tasks/${id}`, patch), wait(EXIT_MS)]);
      data = r.data;
    } catch {
      toast.error("Save failed");
    }
    setExiting((cur) => { const n = { ...cur }; delete n[id]; return n; });
    if (data) {
      applyTask(data);
      setSelectedIds((cur) => { if (!cur.has(id)) return cur; const n = new Set(cur); n.delete(id); return n; });
    }
    return data;
  };

  // One-click Done with Undo — the notes flow still lives in the drawer.
  const quickComplete = async (t) => {
    const before = { status: t.status };
    const after = await exitTask(t.id, "done", { status: "completed" });
    if (!after) return;
    toast.success(`Done: ${shortTitle(t)}`, {
      action: { label: "Undo", onClick: () => updateTask(t.id, before, { silent: true }) },
      duration: 6000,
    });
  };

  const quickReject = async (t) => {
    const before = { status: t.status };
    const after = await exitTask(t.id, "rejected", { status: "rejected" });
    if (!after) return;
    toast(`Rejected: ${shortTitle(t)}`, {
      action: { label: "Undo", onClick: () => updateTask(t.id, before, { silent: true }) },
      duration: 6000,
    });
  };

  const quickReopen = (t) => exitTask(t.id, "reopened", { status: "pending" });

  // Snooze: animate out only when the task actually leaves its current group.
  const snooze = async (t, date) => {
    setMenuFor(null);
    const before = { deadline: t.deadline || null };
    const patch = { deadline: date.toISOString() };
    const moves = bucketOf(t, bounds) !== bucketOf({ deadline: patch.deadline }, bounds);
    const after = moves ? await exitTask(t.id, "snoozed", patch) : await updateTask(t.id, patch, { silent: true });
    if (!after) return;
    toast(`Moved to ${shortWhen(date)}`, {
      action: { label: "Undo", onClick: () => updateTask(t.id, before, { silent: true }) },
      duration: 6000,
    });
  };

  const handleSetDeadline = async (deadline, opts) => {
    const patch = { deadline: deadline ? new Date(deadline).toISOString() : null };
    // Picker tells us whether the user wants an .ics invite emailed for
    // this deadline; undefined = let the backend follow the account setting.
    if (deadline && opts?.sendInvite !== undefined) patch.send_calendar_invite = opts.sendInvite;
    if (bulkRescheduling) {
      await bulkUpdate(patch);
      setBulkRescheduling(false);
      return;
    }
    if (!settingDeadlineTaskId) return;
    await updateTask(settingDeadlineTaskId, patch);
    setSettingDeadlineTaskId(null);
  };

  // Explicit "Add to calendar" — (re)sends the invite for the current deadline.
  const [sendingInviteId, setSendingInviteId] = useState(null);
  const sendCalendarInvite = async (id) => {
    setSendingInviteId(id);
    try {
      const r = await api.post(`/tasks/${id}/calendar-invite`);
      const ci = r.data.calendar_invite;
      setTasks((cur) => cur.map((t) => (t.id === id ? { ...t, calendar_invite: ci } : t)));
      if (focusTask?.id === id) setFocusTask((f) => ({ ...f, calendar_invite: ci }));
      toast.success(`Calendar invite sent to ${(ci?.to || []).join(", ") || "your email"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send calendar invite");
    } finally {
      setSendingInviteId(null);
    }
  };

  const deleteTask = async (id) => {
    const ok = await confirm({
      title: "Delete this task?",
      message: "The task and its history will be removed.",
      confirmLabel: "Delete",
      tone: "danger",
    });
    if (!ok) return;
    await api.delete(`/tasks/${id}`);
    setTasks((cur) => cur.filter((t) => t.id !== id));
    setFocusTask(null);
    toast.success("Deleted");
  };

  // Row delete — already confirmed inline, so no dialog.
  const deleteRow = async (id) => {
    setConfirmDeleteId(null);
    setExiting((cur) => ({ ...cur, [id]: "deleted" }));
    try {
      await Promise.all([api.delete(`/tasks/${id}`), wait(EXIT_MS)]);
      setTasks((cur) => cur.filter((t) => t.id !== id));
    } catch {
      toast.error("Delete failed");
    }
    setExiting((cur) => { const n = { ...cur }; delete n[id]; return n; });
  };

  const toggleSelect = (id, e) => {
    e.stopPropagation();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setShowBulkMenu(false);
    setBulkMenuView("main");
  };

  const bulkUpdate = async (patch) => {
    const ids = [...selectedIds];
    try {
      await Promise.all(ids.map((id) => api.patch(`/tasks/${id}`, patch)));
      setTasks((cur) => cur.map((t) => ids.includes(t.id) ? { ...t, ...patch } : t));
      toast.success(`${ids.length} task${ids.length > 1 ? "s" : ""} updated`);
      clearSelection();
    } catch {
      toast.error("Bulk update failed");
    }
  };

  const mergeTasks = async (primaryId, overrideTitle) => {
    const secondaryIds = [...selectedIds].filter((id) => id !== primaryId);
    try {
      await api.post("/tasks/merge", {
        primary_task_id: primaryId,
        secondary_task_ids: secondaryIds,
        title: overrideTitle || undefined,
      });
      toast.success("Tasks merged");
      clearSelection();
      setShowMergeModal(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Merge failed");
    }
  };

  const selectedTasks = useMemo(
    () => tasks.filter((t) => selectedIds.has(t.id)),
    [tasks, selectedIds],
  );

  const canMerge = useMemo(() => {
    if (selectedTasks.length < 2) return false;
    const chatIds = new Set(selectedTasks.map((t) => t.chat_id ?? "__manual__"));
    return chatIds.size === 1;
  }, [selectedTasks]);

  // ── Derived lists ──────────────────────────────────────────────────────────

  const bounds = useMemo(() => {
    const now = new Date();
    const today = startOfDay(now);
    const tomorrow = addDays(today, 1);
    const dayAfter = addDays(today, 2);
    // "This week" = through the coming Sunday night.
    const weekEnd = addDays(today, 7 - ((today.getDay() + 6) % 7));
    return { now, today, tomorrow, dayAfter, weekEnd: weekEnd <= dayAfter ? addDays(dayAfter, 1) : weekEnd };
  }, [tasks]); // eslint-disable-line react-hooks/exhaustive-deps

  const matchesFilters = (t) => {
    if (selectedType && (isMeeting(t) ? "meeting" : "task") !== selectedType) return false;
    if (selectedPriority && t.priority !== selectedPriority) return false;
    if (selectedAssignee === "__unassigned__" && t.assignee) return false;
    if (selectedAssignee && selectedAssignee !== "__unassigned__" && t.assignee !== selectedAssignee) return false;
    if (onlyInProgress && t.status !== "in_progress") return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      return (t.title || "").toLowerCase().includes(q) ||
        (t.chat_name || "").toLowerCase().includes(q) ||
        (t.sender_name || "").toLowerCase().includes(q) ||
        (t.summary || "").toLowerCase().includes(q) ||
        (t.task_no ? `#${t.task_no}` : "").includes(q);
    }
    return true;
  };

  const activeTasks = useMemo(() => tasks.filter((t) => ACTIVE.has(t.status)), [tasks]);

  // Counts on the summary strip ignore the secondary filters so they always
  // tell the truth about the whole active list.
  const bucketCounts = useMemo(() => {
    const c = { all: activeTasks.length, attention: 0 };
    BUCKETS.forEach((b) => { c[b.key] = 0; });
    activeTasks.forEach((t) => { c[bucketOf(t, bounds)] += 1; });
    c.attention = c.overdue + c.today;
    return c;
  }, [activeTasks, bounds]);

  const viewCounts = useMemo(() => ({
    completed: tasks.filter((t) => t.status === "completed").length,
    rejected: tasks.filter((t) => t.status === "rejected").length,
    in_progress: activeTasks.filter((t) => t.status === "in_progress").length,
  }), [tasks, activeTasks]);

  const sortWithinBucket = (a, b) => {
    const ad = a.deadline ? new Date(a.deadline).getTime() : Infinity;
    const bd = b.deadline ? new Date(b.deadline).getTime() : Infinity;
    if (ad !== bd) return ad - bd;
    return (PRIORITY_ORDER[a.priority] ?? 9) - (PRIORITY_ORDER[b.priority] ?? 9);
  };

  // Grouped active list: [{bucket, tasks}] in urgency order, empty groups dropped.
  const groups = useMemo(() => {
    if (view !== "active") return [];
    const wanted = bucket === "all" ? BUCKETS.map((b) => b.key)
      : bucket === "attention" ? ["overdue", "today"] : [bucket];
    const byBucket = {};
    activeTasks.filter(matchesFilters).forEach((t) => {
      const k = bucketOf(t, bounds);
      (byBucket[k] ||= []).push(t);
    });
    return wanted
      .filter((k) => byBucket[k]?.length)
      .map((k) => ({ ...BUCKETS.find((b) => b.key === k), tasks: byBucket[k].sort(sortWithinBucket) }));
  }, [view, bucket, activeTasks, bounds, selectedType, selectedPriority, selectedAssignee, onlyInProgress, searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const doneList = useMemo(() => {
    if (view === "active") return [];
    return tasks
      .filter((t) => t.status === view)
      .filter(matchesFilters)
      .sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at));
  }, [view, tasks, selectedType, selectedPriority, selectedAssignee, searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const visibleTasks = view === "active" ? groups.flatMap((g) => g.tasks) : doneList;
  const activeFilterCount = [selectedType, selectedPriority, selectedAssignee, filters.category, onlyInProgress ? "x" : ""].filter(Boolean).length;

  const clearFilters = () => {
    setSelectedType(""); setSelectedPriority(""); setSelectedAssignee(""); setOnlyInProgress(false); setSearchQuery("");
    if (filters.category) setFilter("category", "");
  };

  const pickBucket = (key) => {
    setView("active");
    setBucket(key);
    setSelectedIds(new Set());
  };

  const pickView = (v) => {
    setView(v);
    setSelectedIds(new Set());
  };

  const toggleCollapsed = (key) => setCollapsed((cur) => {
    const next = new Set(cur);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  // ── Row ────────────────────────────────────────────────────────────────────

  const EXIT_STYLE = {
    done:     { anim: "sw-exit-approve", tint: "bg-emerald-50/85", pill: "bg-emerald-600", icon: <CheckCircle2 size={13} />, label: "Done" },
    reopened: { anim: "sw-exit-approve", tint: "bg-blue-50/85",    pill: "bg-blue-600",    icon: <RotateCcw size={13} />,    label: "Reopened" },
    snoozed:  { anim: "sw-exit-approve", tint: "bg-amber-50/85",   pill: "bg-amber-600",   icon: <AlarmClock size={13} />,   label: "Snoozed" },
    rejected: { anim: "sw-exit-delete",  tint: "bg-slate-50/85",   pill: "bg-slate-700",   icon: <Ban size={13} />,          label: "Rejected" },
    deleted:  { anim: "sw-exit-delete",  tint: "bg-red-50/85",     pill: "bg-red-600",     icon: <Trash2 size={13} />,       label: "Deleted" },
  };

  const toggleMenu = (id, kind) =>
    setMenuFor((cur) => (cur?.id === id && cur.kind === kind ? null : { id, kind }));

  const renderRow = (t) => {
    const isSelected = selectedIds.has(t.id);
    const isActive = ACTIVE.has(t.status);
    const meeting = isMeeting(t);
    const due = isActive ? dueLabel(t.deadline, bounds, meeting) : null;
    const from = t.chat_type === "group"
      ? `${stripMentionIds(t.chat_name || "")} › ${t.sender_name || ""}`
      : (t.sender_name || t.chat_name || "");
    const ex = exiting[t.id] ? EXIT_STYLE[exiting[t.id]] : null;
    const menu = menuFor?.id === t.id ? menuFor.kind : null;
    const btn = "h-8 px-2.5 inline-flex items-center gap-1.5 rounded-lg border text-[12px] font-semibold transition disabled:opacity-50 whitespace-nowrap";
    const iconBtn = "h-8 w-8 grid place-items-center rounded-lg border transition";
    return (
      <div key={t.id} className={`sw-collapse relative border-b last:border-b-0 border-slate-100 ${ex ? "is-collapsed" : ""} ${menu ? "z-30" : ""}`}>
      <div>
      <div
        className={"group relative flex items-stretch hover:bg-slate-50/80 cursor-pointer transition-colors " + (isSelected ? "bg-blue-50/60 " : "bg-white ") + (ex ? ex.anim + " pointer-events-none" : "sw-fade-up-clean")}
        onClick={() => setFocusTask(t)}
        data-testid={`task-row-${t.id}`}
        data-row
      >
        {ex && (
          <div className={`absolute inset-0 z-10 grid place-items-center sw-fade-in ${ex.tint}`}>
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[12px] font-semibold text-white shadow-sm sw-pop ${ex.pill}`}>{ex.icon} {ex.label}</span>
          </div>
        )}
        <div data-bar className={"w-1 shrink-0 " + (meeting ? "bg-violet-500" : (PRIORITY_BAR[t.priority] || "bg-slate-200"))} title={meeting ? "Meeting" : `${t.priority} priority`} />
        <div className="pl-3 pr-1 py-3 flex items-start" onClick={(e) => e.stopPropagation()}>
          <input type="checkbox" className="mt-1 w-3.5 h-3.5 rounded accent-blue-600 cursor-pointer" checked={isSelected} onChange={(e) => toggleSelect(t.id, e)} />
        </div>
        <div className="flex-1 min-w-0 flex flex-col md:flex-row md:items-center">
        <div className="flex-1 min-w-0 py-2.5 pr-3">
          <div className="flex items-center gap-2 min-w-0">
            {t.task_no && <span className="font-mono text-[11px] text-slate-400 shrink-0">#{t.task_no}</span>}
            <span className="text-[14px] font-semibold text-slate-900 truncate">{t.title}</span>
            {meeting && (
              <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-violet-700 bg-violet-50 border border-violet-200 rounded px-1.5 py-0.5"><Video size={9} /> Meeting</span>
            )}
            {t.status === "in_progress" && (
              <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-blue-700 bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5"><Play size={9} /> In progress</span>
            )}
            {(t.priority === "urgent" || t.priority === "high") && (
              <span className={"shrink-0 text-[10px] font-bold uppercase tracking-wider rounded px-1.5 py-0.5 border " + (t.priority === "urgent" ? "text-red-700 bg-red-50 border-red-200" : "text-amber-700 bg-amber-50 border-amber-200")}>{t.priority}</span>
            )}
          </div>
          {t.summary && <div className="text-[12.5px] text-slate-500 line-clamp-1 mt-0.5">{t.summary}</div>}
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-slate-500">
            {due ? (
              <span className={"inline-flex items-center gap-1 font-semibold " + (due.tone === "red" ? "text-red-600" : due.tone === "amber" ? "text-amber-700" : "text-slate-600")}>
                <Clock size={12} /> {due.text}{due.sub && <span className="font-normal text-slate-400">· {due.sub}</span>}
                {t.deadline_source === "default" && (
                  <span className="ml-1 text-[9.5px] font-bold uppercase tracking-wider text-slate-500 bg-slate-100 border border-slate-200 rounded px-1 py-px" title="Set automatically from the priority — use Snooze to adjust">auto</span>
                )}
              </span>
            ) : isActive ? (
              <button
                onClick={(e) => { e.stopPropagation(); setSettingDeadlineTaskId(t.id); }}
                className="inline-flex items-center gap-1 h-6 px-2 rounded-full border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 text-[11.5px] font-semibold"
                data-testid={`set-deadline-${t.id}`}
              >
                <Calendar size={11} /> Set deadline
              </button>
            ) : (
              <span className="text-slate-400">{t.status === "completed" ? "Completed" : "Rejected"} {formatDateTime(t.updated_at || t.created_at)}</span>
            )}
            {meeting && t.location && <span className="inline-flex items-center gap-1 min-w-0 truncate max-w-[200px]"><MapPin size={12} className="text-slate-400 shrink-0" /><span className="truncate">{t.location}</span></span>}
            {from && <span className="inline-flex items-center gap-1 min-w-0 truncate max-w-[280px]">{t.chat_type === "group" ? <Users size={12} className="text-slate-400 shrink-0" /> : <User size={12} className="text-slate-400 shrink-0" />}<span className="truncate">{from}</span></span>}
            <span className="text-[10px] tracking-[0.14em] uppercase font-bold px-1.5 py-0.5 border rounded-sm" style={{ borderColor: categoryColor(t.category), color: categoryColor(t.category) }}>
              {categoryLabel(t.category)}
            </span>
            {t.assignee
              ? <span className="inline-flex items-center gap-1 text-[11.5px] font-medium text-slate-700 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-full"><User size={10} className="text-slate-400" />{t.assignee}</span>
              : isActive && assignees.length > 0 && <span className="text-[11.5px] text-slate-400 italic">Unassigned</span>}
          </div>
        </div>

        {/* Quick actions — always visible. Under the text on phones, on the right on desktop. */}
        <div className="flex items-center gap-1.5 pr-3 pb-2.5 md:pb-0 md:py-2.5 shrink-0" onClick={(e) => e.stopPropagation()}>
          {isActive ? (
            <>
              <button onClick={() => quickComplete(t)} className={btn + " bg-emerald-600 border-emerald-600 text-white hover:bg-emerald-700 active:scale-95"} title="Mark done" data-testid={`complete-${t.id}`}>
                <CheckCircle2 size={14} /> {meeting ? "Attended" : "Done"}
              </button>

              {meeting ? (
                <button onClick={() => setSettingDeadlineTaskId(t.id)} className={btn + " bg-white border-slate-200 text-slate-700 hover:border-amber-400 hover:text-amber-700"} title="Reschedule" data-testid={`reschedule-${t.id}`}>
                  <Calendar size={13} /> Reschedule
                </button>
              ) : (
                <div className="relative">
                  <button onClick={() => toggleMenu(t.id, "snooze")} className={btn + " bg-white text-slate-700 hover:border-amber-400 hover:text-amber-700 " + (menu === "snooze" ? "border-amber-400 text-amber-700" : "border-slate-200")} title={t.deadline ? "Push the deadline" : "Set a deadline"} data-testid={`snooze-${t.id}`}>
                    <AlarmClock size={13} /> {t.deadline ? "Snooze" : "Schedule"} <ChevronDown size={12} className="-ml-0.5 text-slate-400" />
                  </button>
                  {menu === "snooze" && (
                    <Popover onClose={() => setMenuFor(null)}>
                      {snoozeOptions(new Date()).map((o) => (
                        <MenuItem key={o.key} label={o.label} sub={shortWhen(o.date)} onClick={() => snooze(t, o.date)} testid={`snooze-${o.key}-${t.id}`} />
                      ))}
                      <div className="h-px bg-slate-100 my-1" />
                      <MenuItem icon={<Calendar size={13} />} label="Pick date & time…" onClick={() => { setMenuFor(null); setSettingDeadlineTaskId(t.id); }} testid={`reschedule-${t.id}`} />
                    </Popover>
                  )}
                </div>
              )}

              {assignees.length > 0 && (
                <div className="relative">
                  <button onClick={() => toggleMenu(t.id, "assign")} className={iconBtn + " bg-white text-slate-500 hover:text-slate-900 " + (menu === "assign" ? "border-slate-900" : "border-slate-200 hover:border-slate-900")} title="Assign" data-testid={`assign-${t.id}`}>
                    <UserPlus size={14} />
                  </button>
                  {menu === "assign" && (
                    <Popover width="w-52" onClose={() => setMenuFor(null)}>
                      <MenuItem label="Unassigned" onClick={() => { updateTask(t.id, { assignee: null }); setMenuFor(null); }} />
                      {assignees.map((a) => (
                        <button key={a.id} onClick={() => { updateTask(t.id, { assignee: a.name }); setMenuFor(null); }} className={"w-full text-left px-3 py-1.5 text-[12.5px] hover:bg-slate-50 " + (t.assignee === a.name ? "text-blue-700 font-semibold" : "text-slate-800")}>
                          {a.name}{a.role ? <span className="text-slate-400"> · {a.role}</span> : null}
                        </button>
                      ))}
                    </Popover>
                  )}
                </div>
              )}

              <div className="relative">
                <button onClick={() => toggleMenu(t.id, "more")} className={iconBtn + " bg-white text-slate-500 hover:text-slate-900 " + (menu === "more" ? "border-slate-900" : "border-slate-200 hover:border-slate-900")} title="More" data-testid={`more-${t.id}`}>
                  <MoreHorizontal size={15} />
                </button>
                {menu === "more" && (
                  <Popover onClose={() => setMenuFor(null)}>
                    {t.status === "pending" ? (
                      <MenuItem icon={<Play size={13} />} label="Start (in progress)" onClick={() => { setMenuFor(null); updateTask(t.id, { status: "in_progress" }, { silent: true }); }} testid={`start-${t.id}`} />
                    ) : (
                      <MenuItem icon={<Pause size={13} />} label="Back to pending" onClick={() => { setMenuFor(null); updateTask(t.id, { status: "pending" }, { silent: true }); }} />
                    )}
                    {t.deadline && (
                      <MenuItem
                        icon={inviteIsCurrent(t) ? <CalendarCheck size={13} className="text-emerald-600" /> : <CalendarPlus size={13} />}
                        label={inviteIsCurrent(t) ? "Resend calendar invite" : "Add to calendar"}
                        onClick={() => { setMenuFor(null); sendCalendarInvite(t.id); }}
                        testid={`calendar-invite-${t.id}`}
                      />
                    )}
                    <div className="h-px bg-slate-100 my-1" />
                    <MenuItem icon={<Ban size={13} className="text-red-500" />} label="Reject — not a real task" danger onClick={() => { setMenuFor(null); quickReject(t); }} testid={`reject-${t.id}`} />
                  </Popover>
                )}
              </div>
            </>
          ) : confirmDeleteId === t.id ? (
            <div className="flex items-center gap-1.5 sw-pop" onKeyDown={(e) => e.key === "Escape" && setConfirmDeleteId(null)}>
              <button onClick={() => setConfirmDeleteId(null)} className={btn + " bg-white border-slate-200 text-slate-600 hover:border-slate-400"}>Cancel</button>
              <button autoFocus onClick={() => deleteRow(t.id)} className={btn + " bg-red-600 border-red-600 text-white hover:bg-red-700 active:scale-95"} data-testid={`confirm-delete-${t.id}`}>
                <Trash2 size={13} /> Delete
              </button>
            </div>
          ) : (
            <>
              <button onClick={() => quickReopen(t)} className={btn + " bg-white border-slate-200 text-slate-700 hover:border-blue-400 hover:text-blue-700"} title="Reopen" data-testid={`reopen-${t.id}`}>
                <RotateCcw size={13} /> Reopen
              </button>
              <button onClick={() => setConfirmDeleteId(t.id)} className={iconBtn + " border-slate-200 bg-white text-slate-400 hover:text-red-600 hover:border-red-300"} title="Delete" data-testid={`delete-${t.id}`}>
                <Trash2 size={14} />
              </button>
            </>
          )}
        </div>
        </div>
      </div>
      </div>
      </div>
    );
  };

  const renderGroup = (g) => {
    const tone = TONE[g.tone];
    const isCollapsed = collapsed.has(g.key);
    const allSelected = g.tasks.every((t) => selectedIds.has(t.id));
    return (
      <section key={g.key} className="bg-white border border-slate-200 rounded-xl" data-testid={`group-${g.key}`}>
        <div className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-100 bg-slate-50/60 rounded-t-xl">
          <span className={"w-2 h-2 rounded-full " + tone.bar} />
          <button onClick={() => toggleCollapsed(g.key)} className={"flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.16em] " + tone.head}>
            {g.label} <span className="text-slate-400 font-semibold tracking-normal normal-case">· {g.tasks.length}</span>
            {isCollapsed ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronUp size={14} className="text-slate-400" />}
          </button>
          {g.hint && !isCollapsed && <span className="hidden md:inline text-[12px] text-slate-400">{g.hint}</span>}
          <label className="ml-auto inline-flex items-center gap-1.5 text-[11.5px] text-slate-400 cursor-pointer select-none">
            <input type="checkbox" className="w-3.5 h-3.5 rounded accent-blue-600" checked={g.tasks.length > 0 && allSelected}
              onChange={(e) => {
                if (e.target.checked) setSelectedIds((prev) => new Set([...prev, ...g.tasks.map((t) => t.id)]));
                else setSelectedIds((prev) => { const n = new Set(prev); g.tasks.forEach((t) => n.delete(t.id)); return n; });
              }} />
            select all
          </label>
        </div>
        {!isCollapsed && <div className={ROUNDED_LAST}>{g.tasks.map(renderRow)}</div>}
      </section>
    );
  };

  const chip = (key, label, count, tone, selected, onClick, testid) => (
    <button key={key} onClick={onClick}
      className={"h-9 px-3 shrink-0 inline-flex items-center gap-2 rounded-full border text-[12.5px] font-semibold transition " + (selected ? TONE[tone].chipOn : TONE[tone].chipOff) + (count === 0 && !selected ? " opacity-45" : "")}
      data-testid={testid}>
      {label}
      <span className={"text-[11px] font-bold px-1.5 py-0.5 rounded-full tabular-nums " + (selected ? "bg-white/25" : "bg-white/80 text-slate-700")}>{count}</span>
    </button>
  );

  return (
    <div className="p-4 md:p-8 max-w-[1500px] mx-auto" data-testid="tasks-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-5">
        <div>
          <h1 className="text-[32px] font-extrabold tracking-tight text-slate-900 leading-tight">Tasks</h1>
          <p className="text-[14px] text-slate-500 mt-1">
            {bucketCounts.attention > 0
              ? <><button onClick={() => pickBucket("attention")} className="font-semibold text-red-700 underline decoration-red-300 underline-offset-2 hover:decoration-red-600">{bucketCounts.attention} need{bucketCounts.attention === 1 ? "s" : ""} attention now</button> — overdue or due today. Everything else is lined up below.</>
              : "Nothing overdue or due today. Here's what's coming up."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Search tasks, people, #12…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-10 w-60 pl-9 pr-8 border border-slate-200 rounded-xl text-[13px] focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 outline-none transition bg-white"
              data-testid="task-search-input"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                <X size={13} />
              </button>
            )}
          </div>
          <button
            onClick={() => setShowCreateTask(true)}
            className="h-10 px-5 inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-[13px] font-semibold transition active:scale-95"
            data-testid="create-task-btn"
          >
            <Plus size={16} />
            Create Task
          </button>
        </div>
      </div>

      {/* Review queue nudge — approvals are the other "action needed" pile */}
      {reviewCount > 0 && (
        <Link to="/app/review" className="mb-4 flex items-center gap-3 p-3.5 bg-violet-50 border border-violet-200 rounded-xl hover:border-violet-400 transition group" data-testid="review-banner">
          <div className="w-9 h-9 rounded-lg bg-white border border-violet-200 grid place-items-center shrink-0">
            <ClipboardCheck size={17} className="text-violet-600" />
          </div>
          <div className="flex-1 min-w-0 text-[13px] text-violet-900">
            <span className="font-bold">{reviewCount} item{reviewCount === 1 ? "" : "s"} waiting for your review</span>
            <span className="text-violet-700"> — approve them to turn them into tasks, or reject the noise.</span>
          </div>
          <span className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-violet-700 shrink-0">Review now <ArrowRight size={14} className="group-hover:translate-x-0.5 transition" /></span>
        </Link>
      )}

      {/* Summary strip — one row, every number is a filter */}
      <div className="flex items-center gap-2 mb-3 -mx-4 px-4 overflow-x-auto md:mx-0 md:px-0 md:flex-wrap [scrollbar-width:none]" data-testid="summary-strip">
        {chip("all", "All active", bucketCounts.all, "slate", view === "active" && bucket === "all", () => pickBucket("all"), "bucket-all")}
        {(bucketCounts.attention > 0 || bucket === "attention") && chip("attention", "Needs attention", bucketCounts.attention, "red", view === "active" && bucket === "attention", () => pickBucket("attention"), "bucket-attention")}
        <span className="w-px h-6 bg-slate-200 mx-1 hidden sm:block" />
        {BUCKETS.map((b) => chip(b.key, b.label, bucketCounts[b.key], b.tone, view === "active" && bucket === b.key, () => pickBucket(b.key), `bucket-${b.key}`))}
        <span className="flex-1" />
        <button onClick={() => pickView("completed")} className={"h-9 px-3 shrink-0 rounded-full border text-[12.5px] font-semibold transition " + (view === "completed" ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-200 hover:border-emerald-400")} data-testid="view-completed">
          Done · {viewCounts.completed}
        </button>
        <button onClick={() => pickView("rejected")} className={"h-9 px-3 shrink-0 rounded-full border text-[12.5px] font-semibold transition " + (view === "rejected" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-500 border-slate-200 hover:border-slate-400")} data-testid="view-rejected">
          Rejected · {viewCounts.rejected}
        </button>
      </div>

      {/* Filters — tucked away until needed */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button onClick={() => setShowFilters((v) => !v)} className={"h-8 px-3 inline-flex items-center gap-1.5 rounded-lg border text-[12px] font-semibold transition " + (showFilters || activeFilterCount ? "border-slate-900 text-slate-900 bg-white" : "border-slate-200 text-slate-500 bg-white hover:border-slate-400")} data-testid="toggle-filters">
          <SlidersHorizontal size={13} /> Filters{activeFilterCount ? ` · ${activeFilterCount}` : ""}
        </button>
        {(showFilters || activeFilterCount > 0) && (
          <>
            <Select value={selectedType} onChange={setSelectedType}
              options={[{ value: "", label: "Tasks & meetings" }, { value: "task", label: "Tasks only" }, { value: "meeting", label: "Meetings only" }]} testid="filter-type" />
            <Select value={selectedPriority} onChange={setSelectedPriority}
              options={[{ value: "", label: "Any priority" }, ...[...PRIORITIES].reverse().map((p) => ({ value: p.key, label: p.label }))]} testid="filter-priority" />
            {assignees.length > 0 && (
              <Select value={selectedAssignee} onChange={setSelectedAssignee}
                options={[{ value: "", label: "Anyone" }, { value: "__unassigned__", label: "Unassigned" }, ...assignees.map((a) => ({ value: a.name, label: a.name + (a.role ? ` · ${a.role}` : "") }))]} testid="filter-assignee" />
            )}
            <Select value={filters.category} onChange={(v) => setFilter("category", v)}
              options={[{ value: "", label: "All categories" }, ...CATEGORIES.map((c) => ({ value: c.key, label: c.label }))]} testid="filter-category" />
            {view === "active" && (
              <label className="h-8 px-3 inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white text-[12px] text-slate-600 cursor-pointer select-none">
                <input type="checkbox" className="w-3.5 h-3.5 accent-blue-600" checked={onlyInProgress} onChange={(e) => setOnlyInProgress(e.target.checked)} data-testid="filter-in-progress" />
                In progress only{viewCounts.in_progress ? ` (${viewCounts.in_progress})` : ""}
              </label>
            )}
            {activeFilterCount > 0 && (
              <button onClick={clearFilters} className="h-8 px-2 text-[12px] text-slate-500 hover:text-red-600 inline-flex items-center gap-1"><X size={12} /> Clear</button>
            )}
          </>
        )}
        <span className="ml-auto text-[12px] text-slate-400 tabular-nums">{visibleTasks.length} shown</span>
      </div>

      {/* List */}
      <div data-testid="tasks-table" className="space-y-4">
        {loading && (
          <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-500">Loading…</div>
        )}

        {!loading && view === "active" && groups.length === 0 && (
          <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
            {activeTasks.length === 0 ? (
              <>
                <Inbox size={40} className="text-slate-300 mx-auto mb-3" />
                <div className="text-slate-800 font-semibold text-[15px] mb-1">No active tasks</div>
                <div className="text-slate-500 text-[13px]">New tasks appear here as Brainydo reads your chats. You can also create one.</div>
              </>
            ) : bucket === "attention" || bucket === "overdue" || bucket === "today" ? (
              <>
                <span className="material-symbols-outlined text-[48px] text-emerald-400 mb-3 block" style={{ fontVariationSettings: "'FILL' 1" }}>task_alt</span>
                <div className="text-emerald-700 font-semibold text-[15px] mb-1">You're all caught up!</div>
                <div className="text-slate-500 text-[13px]">Nothing overdue or due today. <button onClick={() => pickBucket("all")} className="text-blue-600 font-semibold underline">See everything active</button>.</div>
              </>
            ) : (
              <>
                <div className="text-slate-800 font-semibold text-[15px] mb-1">Nothing here</div>
                <div className="text-slate-500 text-[13px]">No tasks match this slice{activeFilterCount ? " and your filters" : ""}. {activeFilterCount ? <button onClick={clearFilters} className="text-blue-600 font-semibold underline">Clear filters</button> : <button onClick={() => pickBucket("all")} className="text-blue-600 font-semibold underline">Show all active</button>}.</div>
              </>
            )}
          </div>
        )}

        {!loading && view === "active" && groups.map(renderGroup)}

        {!loading && view !== "active" && (
          doneList.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-500">
              No {view} tasks{activeFilterCount ? " match your filters" : " yet"}.
            </div>
          ) : (
            <section className="bg-white border border-slate-200 rounded-xl">
              <div className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-100 bg-slate-50/60 rounded-t-xl">
                <span className={"w-2 h-2 rounded-full " + (view === "completed" ? "bg-emerald-500" : "bg-slate-400")} />
                <span className={"text-[12px] font-bold uppercase tracking-[0.16em] " + (view === "completed" ? "text-emerald-700" : "text-slate-600")}>{view === "completed" ? "Done" : "Rejected"} <span className="text-slate-400 font-semibold tracking-normal normal-case">· {doneList.length} · newest first</span></span>
              </div>
              <div className={ROUNDED_LAST}>{doneList.map(renderRow)}</div>
            </section>
          )
        )}
      </div>

      {/* Bulk Action Bar */}
      {selectedIds.size >= 1 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-30 flex flex-col items-center gap-2">
          {/* Actions menu — floats above the bar */}
          {showBulkMenu && (
            <>
              <div className="fixed inset-0 z-[-1]" onClick={() => setShowBulkMenu(false)} />
              <div className="bg-white border border-slate-200 rounded-2xl shadow-2xl overflow-hidden w-60">
                {bulkMenuView === "main" ? (
                  <div className="py-1.5">
                    <BulkMenuItem icon={<CheckCircle2 size={14} className="text-emerald-600" />} label="Mark Complete" onClick={() => { bulkUpdate({ status: "completed" }); setShowBulkMenu(false); }} />
                    <BulkMenuItem icon={<span className="material-symbols-outlined text-[14px] text-blue-600 leading-none" style={{ fontVariationSettings: "'FILL' 1" }}>autorenew</span>} label="Mark In Progress" onClick={() => { bulkUpdate({ status: "in_progress" }); setShowBulkMenu(false); }} />
                    <BulkMenuItem icon={<X size={14} className="text-red-500" />} label="Reject" onClick={() => { bulkUpdate({ status: "rejected" }); setShowBulkMenu(false); }} />
                    <div className="h-px bg-slate-100 my-1" />
                    <BulkMenuItem icon={<Calendar size={14} className="text-amber-600" />} label="Reschedule" onClick={() => { setShowBulkMenu(false); setBulkRescheduling(true); }} />
                    <BulkMenuItem icon={<User size={14} className="text-slate-500" />} label="Assign To" hasArrow onClick={() => setBulkMenuView("assign")} />
                    <BulkMenuItem icon={<AlertTriangle size={14} className="text-slate-500" />} label="Change Priority" hasArrow onClick={() => setBulkMenuView("priority")} />
                    <div className="h-px bg-slate-100 my-1" />
                    <BulkMenuItem
                      icon={<GitMerge size={14} className={canMerge ? "text-blue-600" : "text-slate-300"} />}
                      label="Merge Tasks"
                      disabled={!canMerge}
                      hint={!canMerge ? (selectedIds.size < 2 ? "Need 2+" : "Same chat only") : null}
                      onClick={() => { if (canMerge) { setShowBulkMenu(false); setShowMergeModal(true); } }}
                    />
                  </div>
                ) : bulkMenuView === "assign" ? (
                  <div className="py-1.5">
                    <button onClick={() => setBulkMenuView("main")} className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] text-slate-500 hover:text-slate-900 transition w-full">
                      <ChevronDown size={13} className="rotate-90" /> Back
                    </button>
                    <div className="h-px bg-slate-100 my-1" />
                    <BulkMenuItem label="Unassigned" onClick={() => { bulkUpdate({ assignee: null }); setShowBulkMenu(false); setBulkMenuView("main"); }} />
                    {assignees.map((a) => (
                      <BulkMenuItem key={a.id} label={a.name + (a.role ? ` · ${a.role}` : "")} onClick={() => { bulkUpdate({ assignee: a.name }); setShowBulkMenu(false); setBulkMenuView("main"); }} />
                    ))}
                    {assignees.length === 0 && <div className="px-3 py-2 text-[12px] text-slate-400">No assignees configured</div>}
                  </div>
                ) : bulkMenuView === "priority" ? (
                  <div className="py-1.5">
                    <button onClick={() => setBulkMenuView("main")} className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] text-slate-500 hover:text-slate-900 transition w-full">
                      <ChevronDown size={13} className="rotate-90" /> Back
                    </button>
                    <div className="h-px bg-slate-100 my-1" />
                    {PRIORITIES.map((p) => (
                      <BulkMenuItem key={p.key} label={p.label} onClick={() => { bulkUpdate({ priority: p.key }); setShowBulkMenu(false); setBulkMenuView("main"); }} />
                    ))}
                  </div>
                ) : null}
              </div>
            </>
          )}

          {/* Bar */}
          <div className="bg-[#0F172A] text-white rounded-2xl px-5 py-3 flex items-center gap-4 shadow-2xl border border-slate-700 sw-fade-up">
            <span className="text-[13px] font-semibold tabular-nums">{selectedIds.size} selected</span>
            <div className="w-px h-4 bg-slate-600" />
            <button
              onClick={() => { setShowBulkMenu((v) => !v); setBulkMenuView("main"); }}
              className="h-8 px-4 bg-blue-600 hover:bg-blue-500 rounded-xl text-[13px] font-semibold transition flex items-center gap-2 active:scale-95"
            >
              Actions
              {showBulkMenu ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
            </button>
            <button onClick={clearSelection} className="text-slate-400 hover:text-white transition">
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Task Drawer */}
      {focusTask && (
        <TaskDrawer
          task={focusTask}
          assignees={assignees}
          onClose={() => { setFocusTask(null); const np = new URLSearchParams(params); np.delete("focus"); setParams(np); }}
          onUpdate={(p) => updateTask(focusTask.id, p)}
          onDelete={() => deleteTask(focusTask.id)}
          onSendInvite={() => sendCalendarInvite(focusTask.id)}
          sendingInvite={sendingInviteId === focusTask.id}
        />
      )}

      {/* Create Task Drawer */}
      {showCreateTask && (
        <CreateTaskDrawer
          assignees={assignees}
          onClose={() => setShowCreateTask(false)}
          onTaskCreated={() => { setShowCreateTask(false); load(); }}
        />
      )}

      {/* Deadline Picker Modal — single task or bulk reschedule */}
      {(settingDeadlineTaskId || bulkRescheduling) && (
        <DeadlinePicker
          taskId={settingDeadlineTaskId || null}
          onSelect={handleSetDeadline}
          onCancel={() => { setSettingDeadlineTaskId(null); setBulkRescheduling(false); }}
          currentDeadline={
            settingDeadlineTaskId
              ? tasks.find((t) => t.id === settingDeadlineTaskId)?.deadline
              : null
          }
        />
      )}

      {/* Merge Modal */}
      {showMergeModal && (
        <MergeModal
          tasks={selectedTasks}
          onMerge={mergeTasks}
          onCancel={() => setShowMergeModal(false)}
        />
      )}
    </div>
  );
}


function MergeModal({ tasks, onMerge, onCancel }) {
  const [primaryId, setPrimaryId] = useState(tasks[0]?.id ?? "");
  const [title, setTitle] = useState(tasks[0]?.title ?? "");
  const [merging, setMerging] = useState(false);

  // Keep title in sync when primary selection changes
  const handlePrimaryChange = (id) => {
    setPrimaryId(id);
    const t = tasks.find((x) => x.id === id);
    if (t) setTitle(t.title);
  };

  const confirm = async () => {
    setMerging(true);
    await onMerge(primaryId, title);
    setMerging(false);
  };

  const secondaries = tasks.filter((t) => t.id !== primaryId);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="merge-modal">
      <div className="bg-white border border-slate-200 rounded-xl shadow-xl w-full max-w-lg">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <GitMerge size={18} className="text-blue-600" />
            <h2 className="text-[17px] font-bold text-slate-900">Merge {tasks.length} Tasks</h2>
          </div>
          <button onClick={onCancel} className="h-8 w-8 grid place-items-center border border-slate-200 hover:border-slate-900 rounded-lg transition">
            <X size={16} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Pick primary */}
          <div>
            <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-2 block">Keep as Primary Task</label>
            <div className="space-y-2">
              {tasks.map((t) => (
                <label
                  key={t.id}
                  className={"flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition " +
                    (primaryId === t.id ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300")}
                >
                  <input
                    type="radio"
                    name="primary"
                    value={t.id}
                    checked={primaryId === t.id}
                    onChange={() => handlePrimaryChange(t.id)}
                    className="mt-0.5 accent-blue-600"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold text-slate-900 truncate">{t.title}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">{t.chat_name || "Manual task"} · {t.priority}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Override title */}
          <div>
            <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-2 block">Merged Task Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full h-10 px-3 border border-slate-200 rounded-lg text-[13.5px] focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 outline-none transition"
            />
          </div>

          {/* What will happen */}
          {secondaries.length > 0 && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-[12px] text-amber-800">
              <span className="font-semibold">Note:</span> The following {secondaries.length === 1 ? "task" : "tasks"} will be deleted after merging:{" "}
              {secondaries.map((t) => `"${t.title}"`).join(", ")}. Their content will be preserved in the primary task's notes.
            </div>
          )}

          <div className="flex gap-2 pt-2 border-t border-slate-200">
            <button
              onClick={confirm}
              disabled={merging || !primaryId}
              className="flex-1 h-10 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-[14px] font-semibold transition active:scale-95 disabled:opacity-60 flex items-center justify-center gap-2"
            >
              <GitMerge size={15} />
              {merging ? "Merging…" : "Confirm Merge"}
            </button>
            <button onClick={onCancel} className="flex-1 h-10 border border-slate-200 hover:border-slate-900 rounded-xl text-[14px] transition">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


function CreateTaskDrawer({ onClose, onTaskCreated, assignees = [] }) {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    title: "",
    category: "task",
    priority: "medium",
    assignee: "",
    deadline: "",
    notes: "",
    item_type: "task",
    duration_minutes: 30,
    location: "",
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/tasks/create", {
        title: form.title,
        category: form.category,
        priority: form.priority,
        assignee: form.assignee || null,
        status: "pending",
        deadline: form.deadline ? new Date(form.deadline).toISOString() : null,
        item_type: form.item_type,
        duration_minutes: form.item_type === "meeting" ? form.duration_minutes : null,
        location: form.item_type === "meeting" ? (form.location || null) : null,
        notes: form.notes,
      });
      toast.success("Task created");
      onTaskCreated();
    } catch (err) {
      console.error("Task creation error:", err);
      const errMsg = err.response?.data?.detail || err.message || "Failed to create task";
      toast.error(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40" data-testid="create-task-drawer">
      <div className="absolute inset-0 bg-slate-900/30" onClick={onClose} />
      <aside className="absolute right-0 top-0 h-full w-full max-w-md bg-white border-l border-slate-200 overflow-y-auto sw-fade-up">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <div className="label-eyebrow">Create task</div>
          <button
            onClick={onClose}
            className="h-8 w-8 grid place-items-center border border-slate-200 hover:border-slate-900 rounded-sm"
            data-testid="create-task-close"
          >
            <X size={14} />
          </button>
        </div>

        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          <div>
            <label className="label-eyebrow">Title</label>
            <input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Task title"
              className="mt-1.5 w-full h-10 px-3 border border-slate-200 rounded-sm text-[13.5px] focus:border-slate-900 outline-none"
              required
            />
          </div>

          <div>
            <label className="label-eyebrow">Type</label>
            <div className="mt-1.5 grid grid-cols-2 gap-2">
              {[{ v: "task", l: "Task", h: "Work due by a time" }, { v: "meeting", l: "Meeting", h: "Time spent with someone" }].map((o) => (
                <button type="button" key={o.v} onClick={() => setForm({ ...form, item_type: o.v })}
                  className={"h-14 px-3 border-2 rounded-lg text-left transition " + (form.item_type === o.v ? "border-violet-500 bg-violet-50/60" : "border-slate-200 hover:border-slate-400")}
                  data-testid={`create-type-${o.v}`}>
                  <div className="text-[13px] font-semibold text-slate-900">{o.l}</div>
                  <div className="text-[11px] text-slate-500">{o.h}</div>
                </button>
              ))}
            </div>
          </div>

          {form.item_type === "meeting" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-eyebrow">How long</label>
                <select value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: Number(e.target.value) })}
                  className="mt-1.5 w-full h-10 px-2 border border-slate-200 rounded-sm bg-white text-[13.5px] focus:border-slate-900 outline-none" data-testid="create-duration">
                  {[15, 30, 45, 60, 90, 120, 180, 240].map((m) => (
                    <option key={m} value={m}>{m < 60 ? `${m} min` : `${m / 60} hour${m === 60 ? "" : "s"}`}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label-eyebrow">Where (optional)</label>
                <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Office, address or link"
                  className="mt-1.5 w-full h-10 px-2 border border-slate-200 rounded-sm text-[13.5px] focus:border-slate-900 outline-none" data-testid="create-location" />
              </div>
            </div>
          )}

          <div>
            <label className="label-eyebrow">Category</label>
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="mt-1.5 w-full h-10 px-2 border border-slate-200 rounded-sm bg-white text-[13.5px] focus:border-slate-900 outline-none"
            >
              {CATEGORIES.map((c) => (
                <option key={c.key} value={c.key}>{c.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="label-eyebrow">Priority</label>
            <select
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value })}
              className="mt-1.5 w-full h-10 px-2 border border-slate-200 rounded-sm bg-white text-[13.5px] focus:border-slate-900 outline-none"
            >
              {PRIORITIES.map((p) => (
                <option key={p.key} value={p.key}>{p.label}</option>
              ))}
            </select>
          </div>

          {assignees.length > 0 && (
            <div>
              <label className="label-eyebrow">Assign To (optional)</label>
              <select
                value={form.assignee}
                onChange={(e) => setForm({ ...form, assignee: e.target.value })}
                className="mt-1.5 w-full h-10 px-2 border border-slate-200 rounded-sm bg-white text-[13.5px] focus:border-slate-900 outline-none"
              >
                <option value="">— Unassigned —</option>
                {assignees.map((a) => (
                  <option key={a.id} value={a.name}>{a.name}{a.role ? ` · ${a.role}` : ""}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="label-eyebrow">Deadline (optional)</label>
            <input
              type="datetime-local"
              value={form.deadline}
              onChange={(e) => setForm({ ...form, deadline: e.target.value })}
              min={new Date().toISOString().slice(0, 16)}
              className="mt-1.5 w-full h-10 px-2 border border-slate-200 rounded-sm text-[13.5px] focus:border-slate-900 outline-none"
            />
            <p className="text-[11.5px] text-slate-400 mt-1">Leave blank and Brainydo sets one from the priority (urgent 4 h, high 1 day, medium 2 days, low 1 week — adjustable in Settings).</p>
          </div>

          <div>
            <label className="label-eyebrow">Notes (optional)</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Add any notes..."
              rows={4}
              className="mt-1.5 w-full p-2 border border-slate-200 rounded-sm text-[13.5px] focus:border-slate-900 outline-none resize-y"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full h-10 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-[14px] font-semibold disabled:opacity-60 mt-6 transition active:scale-95"
            data-testid="create-task-submit"
          >
            {submitting ? "Creating…" : "Create Task"}
          </button>
        </form>
      </aside>
    </div>
  );
}

function MenuItem({ icon, label, sub, onClick, danger, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={"w-full flex items-center gap-2.5 px-3 py-1.5 text-left text-[12.5px] hover:bg-slate-50 " + (danger ? "text-red-600" : "text-slate-800")}>
      {icon && <span className="w-4 shrink-0 grid place-items-center text-slate-400">{icon}</span>}
      <span className="flex-1">{label}</span>
      {sub && <span className="text-[11px] text-slate-400 tabular-nums">{sub}</span>}
    </button>
  );
}

function Popover({ children, onClose, width = "w-56" }) {
  return (
    <>
      <div className="fixed inset-0 z-20" onClick={onClose} />
      <div className={`absolute right-0 top-9 z-30 ${width} bg-white border border-slate-200 rounded-xl shadow-xl py-1.5 max-h-72 overflow-y-auto sw-pop`}>
        {children}
      </div>
    </>
  );
}

function BulkMenuItem({ icon, label, onClick, disabled, hint, hasArrow }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={"flex items-center gap-2.5 w-full px-3 py-2 text-[13px] text-left transition " +
        (disabled ? "text-slate-300 cursor-default" : "text-slate-700 hover:bg-slate-50 hover:text-slate-900")}
    >
      {icon && <span className="shrink-0 w-4 flex items-center justify-center">{icon}</span>}
      <span className="flex-1">{label}</span>
      {hint && <span className="text-[11px] text-slate-400">{hint}</span>}
      {hasArrow && <ChevronDown size={13} className="text-slate-400 -rotate-90 shrink-0" />}
    </button>
  );
}

